import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  TextField,
  Stack,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Home as HomeIcon,
  CalendarToday as CalendarIcon,
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
  Assignment as AssignmentIcon,
  SupervisorAccount as ManagerIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import customerService from '../../services/customerService';
import appointmentService from '../../services/appointmentService';
import assessmentService from '../../services/assessmentService';
import AssignCustomerDialog from '../../components/customers/AssignCustomerDialog';
import { useAuth } from '../../hooks/useAuth';

const CustomerDetail = ({ isEditing = false, isNew = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  
  // Edit mode state
  const [editMode, setEditMode] = useState(isEditing);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    notes: '',
    source: 'website' // Default source for manually created customers (must be one of the valid enum values)
  });
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Fetch customer data or initialize for new customer
  useEffect(() => {
    // If creating a new customer, just set up the form
    if (isNew) {
      setCustomer(null);
      // Initialize form with empty values and valid source
      setFormData({
        name: '',
        email: '',
        phone: '',
        address: '',
        notes: '',
        source: 'website' // Valid enum value from the backend model
      });
      setLoading(false);
      return;
    }
    
    // Otherwise fetch existing customer
    const fetchCustomer = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await customerService.getCustomerById(id);
        setCustomer(data);
        
        // Initialize form data for editing
        setFormData({
          name: data.name || '',
          email: data.email || '',
          phone: data.phone || '',
          address: data.address || '',
          notes: data.notes || '',
          source: data.source || 'manual'
        });
      } catch (err) {
        console.error(`Error fetching customer ${id}:`, err);
        setError('Failed to load customer data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchCustomer();
    }
  }, [id, isNew]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Related records for the Appointments / Assessments tabs. There is no
  // by-customer endpoint yet, so we fetch the lists and filter client-side,
  // lazily, the first time each tab is opened.
  const [related, setRelated] = useState({
    appointments: { loaded: false, loading: false, error: false, items: [] },
    assessments: { loaded: false, loading: false, error: false, items: [] },
  });

  useEffect(() => {
    if (isNew || !id) return;

    const belongsToCustomer = (record) =>
      String(record.customer?._id || record.customer) === String(id);

    const load = async (key, fetcher, sortKey) => {
      setRelated((prev) => ({ ...prev, [key]: { ...prev[key], loading: true } }));
      try {
        const all = await fetcher();
        const items = (all || [])
          .filter(belongsToCustomer)
          .sort((a, b) => new Date(b[sortKey]) - new Date(a[sortKey]));
        setRelated((prev) => ({
          ...prev,
          [key]: { loaded: true, loading: false, error: false, items },
        }));
      } catch (err) {
        console.error(`Error loading customer ${key}:`, err);
        setRelated((prev) => ({
          ...prev,
          [key]: { loaded: true, loading: false, error: true, items: [] },
        }));
      }
    };

    if (tabValue === 1 && !related.appointments.loaded && !related.appointments.loading) {
      load('appointments', appointmentService.getAppointments, 'date');
    }
    if (tabValue === 2 && !related.assessments.loaded && !related.assessments.loading) {
      load('assessments', assessmentService.getAssessments, 'createdAt');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue, id, isNew]);

  // Handle sending to CRM
  const handleSendToCrm = async () => {
    try {
      await customerService.sendToCrm(id);
      // Update local state to reflect the change
      setCustomer(prev => ({ ...prev, sentToCrm: true }));
    } catch (err) {
      console.error('Error sending to CRM:', err);
      // Handle error
    }
  };
  
  // Handle assignment dialog
  const handleOpenAssignDialog = () => {
    setAssignDialogOpen(true);
  };
  
  const handleCloseAssignDialog = () => {
    setAssignDialogOpen(false);
  };
  
  // Handle successful assignment
  const handleAssignSuccess = async () => {
    try {
      // Refresh customer data to get updated assignment
      const updatedCustomer = await customerService.getCustomerById(id);
      setCustomer(updatedCustomer);
    } catch (err) {
      console.error('Error refreshing customer data:', err);
    }
  };
  
  // Check if user can assign customers (admin or manager)
  const canAssignCustomers = () => {
    return currentUser && (currentUser.role === 'admin' || currentUser.role === 'manager');
  };
  
  // Check if user can edit customers (admin or manager, but not claims_manager)
  const canEditCustomers = () => {
    return currentUser && (currentUser.role === 'admin' || currentUser.role === 'manager');
  };
  
  // Edit mode handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleEnableEdit = () => {
    setEditMode(true);
  };
  
  const handleCancelEdit = () => {
    // For new customer, just go back to the listing
    if (isNew) {
      navigate('/customers');
      return;
    }
    
    // Reset form data to original customer data
    if (customer) {
      setFormData({
        name: customer.name || '',
        email: customer.email || '',
        phone: customer.phone || '',
        address: customer.address || '',
        notes: customer.notes || '',
        source: customer.source || 'manual'
      });
    }
    
    // If we came here directly via the edit route, go back to detail view
    if (isEditing) {
      navigate(`/customers/${id}`);
    } else {
      setEditMode(false);
    }
    
    // Clear any save errors
    setSaveError(null);
  };
  
  const validateFormData = () => {
    const errors = [];
    if (!formData.name || formData.name.trim() === '') {
      errors.push('Name is required');
    }
    if (!formData.email || formData.email.trim() === '') {
      errors.push('Email is required');
    } else if (!/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(formData.email)) {
      errors.push('Please enter a valid email address');
    }
    if (!formData.phone || formData.phone.trim() === '') {
      errors.push('Phone number is required');
    }
    if (!formData.address || formData.address.trim() === '') {
      errors.push('Address is required');
    }
    
    return errors;
  };

  const handleSaveCustomer = async () => {
    try {
      setSaveLoading(true);
      setSaveError(null);
      
      // Validate form data
      const validationErrors = validateFormData();
      if (validationErrors.length > 0) {
        setSaveError(validationErrors.join('. '));
        setSaveLoading(false);
        return;
      }
      
      if (isNew) {
        // Create new customer with proper structure
        const customerData = {
          name: formData.name.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          address: formData.address.trim(),
          notes: formData.notes || '',
          source: 'website' // Use 'website' as it's one of the valid enum values
        };
        
        const createdCustomer = await customerService.createCustomer(customerData);
        navigate(`/customers/${createdCustomer._id}`);
      } else {
        // Update existing customer
        const updatedCustomer = await customerService.updateCustomer(id, formData);
        
        // Update local state with updated customer data
        setCustomer(updatedCustomer);
        
        // Exit edit mode and navigate back to detail view if needed
        setEditMode(false);
        if (isEditing) {
          navigate(`/customers/${id}`);
        }
      }
    } catch (err) {
      console.error('Error saving customer:', err);
      
      // Handle different error cases
      if (err.message && err.message.includes('Network error')) {
        // Network connection issue
        setSaveError('Network connection error. Please check your internet connection and try again.');
      } else if (err.response && err.response.status === 401) {
        // Authentication error
        setSaveError('Your session has expired. Please log in again.');
        // Redirect to login page after a short delay
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else if (err.response && err.response.status === 403) {
        // Permission error
        setSaveError('You do not have permission to perform this action.');
      } else if (err.response && err.response.data && err.response.data.error) {
        // Show specific error from server if available
        setSaveError(err.response.data.error);
      } else if (err.message) {
        // Use the error message if available
        setSaveError(err.message);
      } else {
        // Generic error message
        setSaveError(isNew ? 'Failed to create customer. Please try again.' : 'Failed to update customer. Please try again.');
      }
    } finally {
      setSaveLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/customers')}
          sx={{ mb: 3 }}
        >
          Back to Customers
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!customer && !isNew) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/customers')}
          sx={{ mb: 3 }}
        >
          Back to Customers
        </Button>
        <Alert severity="warning">Customer not found</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1.5,
          mb: { xs: 2.5, md: 3.5 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: '1 1 auto', minWidth: 0 }}>
          <IconButton
            aria-label="Back to customers"
            onClick={() => navigate('/customers')}
            sx={{
              border: (t) => `1px solid ${t.palette.divider}`,
              backgroundColor: 'background.paper',
              flexShrink: 0,
            }}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography
            variant="h3"
            component="h1"
            noWrap
            sx={{ fontSize: { xs: '1.45rem', md: '1.75rem' } }}
          >
            {isNew ? 'Add New Customer' : 'Customer Details'}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.25,
            width: { xs: '100%', md: 'auto' },
            '& > *': { flex: { xs: '1 1 auto', md: '0 0 auto' } },
          }}
        >
          {!isNew && !editMode && (
            <>
              {canAssignCustomers() && (
                <Button
                  variant="outlined"
                  color="inherit"
                  startIcon={<AssignmentIcon />}
                  onClick={handleOpenAssignDialog}
                  sx={{ borderColor: 'divider', bgcolor: 'background.paper' }}
                >
                  {customer?.assignedTo ? 'Reassign' : 'Assign'}
                </Button>
              )}
              {canEditCustomers() && (
                <Button
                  variant="outlined"
                  color="inherit"
                  startIcon={<EditIcon />}
                  onClick={handleEnableEdit}
                  sx={{ borderColor: 'divider', bgcolor: 'background.paper' }}
                >
                  Edit
                </Button>
              )}
              <Button
                variant="contained"
                color="primary"
                startIcon={<SendIcon />}
                disabled={customer?.sentToCrm}
                onClick={handleSendToCrm}
              >
                {customer?.sentToCrm ? 'Sent to CRM' : 'Send to CRM'}
              </Button>
            </>
          )}

          {(!isNew && editMode) && (
            <>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<CancelIcon />}
                onClick={handleCancelEdit}
                sx={{ borderColor: 'divider', bgcolor: 'background.paper' }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={handleSaveCustomer}
                disabled={saveLoading}
              >
                {saveLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </>
          )}
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Customer overview card */}
        <Grid item xs={12} md={isNew ? 12 : 4}>
          {isNew ? (
            <Card sx={{ height: '100%', boxShadow: 2, width: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                  Create New Customer
                </Typography>
                
                {saveError && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    <Typography variant="body2" fontWeight="500">Error:</Typography>
                    {saveError.split('. ').map((error, index) => (
                      <Typography key={index} variant="body2">{error}</Typography>
                    ))}
                  </Alert>
                )}
                
                <Stack spacing={3} sx={{ maxWidth: '800px', mx: 'auto' }}>
                  <TextField
                    label="Name"
                    fullWidth
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon color="primary" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  
                  <TextField
                    label="Email"
                    type="email"
                    fullWidth
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon color="primary" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  
                  <TextField
                    label="Phone"
                    fullWidth
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIcon color="primary" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  
                  <TextField
                    label="Address"
                    fullWidth
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    multiline
                    rows={2}
                    InputProps={{
                      startAdornment: (
                        <Box sx={{ mt: 1.5, mr: 1 }}>
                          <HomeIcon color="primary" />
                        </Box>
                      ),
                    }}
                  />
                  
                  <TextField
                    label="Notes"
                    fullWidth
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    multiline
                    rows={3}
                    InputProps={{
                      startAdornment: (
                        <Box sx={{ mt: 1.5, mr: 1 }}>
                          <AssignmentIcon color="primary" />
                        </Box>
                      ),
                    }}
                  />
                  
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<CancelIcon />}
                      onClick={handleCancelEdit}
                      sx={{ mr: 2 }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<SaveIcon />}
                      onClick={handleSaveCustomer}
                      disabled={saveLoading}
                    >
                      {saveLoading ? 'Creating...' : 'Create Customer'}
                    </Button>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          ) : (
            <Card sx={{ height: '100%', boxShadow: 1 }}>
              <CardContent>
                {!editMode ? (
                  <>
                    <Box display="flex" alignItems="center" mb={2}>
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'primary.light',
                          color: 'white',
                          mr: 2,
                        }}
                      >
                        <Typography variant="h4" component="span">
                          {customer?.name ? customer.name.charAt(0).toUpperCase() : 'C'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="h5" component="h2" fontWeight="500">
                          {customer?.name}
                        </Typography>
                        <Chip
                          label={customer?.source || 'Website'}
                          size="small"
                          color={
                            customer?.source === 'assessment'
                              ? 'success'
                              : customer?.source === 'appointment'
                                ? 'secondary'
                                : 'primary'
                          }
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <List disablePadding>
                      <ListItem sx={{ px: 0, py: 1 }}>
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center">
                              <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                              <Typography variant="body2" fontWeight="500">
                                Email
                              </Typography>
                            </Box>
                          }
                          secondary={customer?.email}
                        />
                      </ListItem>
                      <ListItem sx={{ px: 0, py: 1 }}>
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center">
                              <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />
                              <Typography variant="body2" fontWeight="500">
                                Phone
                              </Typography>
                            </Box>
                          }
                          secondary={customer?.phone}
                        />
                      </ListItem>
                      <ListItem sx={{ px: 0, py: 1 }}>
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center">
                              <HomeIcon sx={{ mr: 1, color: 'text.secondary' }} />
                              <Typography variant="body2" fontWeight="500">
                                Address
                              </Typography>
                            </Box>
                          }
                          secondary={customer?.address || 'Not provided'}
                        />
                      </ListItem>
                    </List>
                  </>
                ) : (
                  <>
                    <Typography variant="h6" gutterBottom>Edit Customer Information</Typography>
                    
                    {saveError && (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        <Typography variant="body2" fontWeight="500">Error:</Typography>
                        {saveError.split('. ').map((error, index) => (
                          <Typography key={index} variant="body2">{error}</Typography>
                        ))}
                      </Alert>
                    )}
                    
                    <Stack spacing={3}>
                      <TextField
                        label="Name"
                        fullWidth
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                      />
                      
                      <TextField
                        label="Email"
                        type="email"
                        fullWidth
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                      />
                      
                      <TextField
                        label="Phone"
                        fullWidth
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                      />
                      
                      <TextField
                        label="Address"
                        fullWidth
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        multiline
                        rows={2}
                      />
                      
                      <TextField
                        label="Notes"
                        fullWidth
                        name="notes"
                        value={formData.notes}
                        onChange={handleInputChange}
                        multiline
                        rows={3}
                      />
                    </Stack>
                  </>
                )}
                  
                {!editMode && customer && (
                  <List disablePadding>
                    <ListItem sx={{ px: 0, py: 1 }}>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center">
                            <CalendarIcon sx={{ mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body2" fontWeight="500">
                              Created On
                            </Typography>
                          </Box>
                        }
                        secondary={
                          customer.createdAt
                            ? format(new Date(customer.createdAt), 'MMMM dd, yyyy')
                            : 'Unknown'
                        }
                      />
                    </ListItem>
                    {customer.assignedTo && (
                      <ListItem sx={{ px: 0, py: 1 }}>
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center">
                              <ManagerIcon sx={{ mr: 1, color: 'text.secondary' }} />
                              <Typography variant="body2" fontWeight="500">
                                Assigned To
                              </Typography>
                            </Box>
                          }
                          secondary={
                            typeof customer.assignedTo === 'object' && customer.assignedTo?.name 
                              ? customer.assignedTo.name
                              : 'User'
                          }
                        />
                      </ListItem>
                    )}
                  </List>
                )}
                
                {!editMode && customer && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Box mt={2}>
                      <Button
                        variant="outlined"
                        fullWidth
                        onClick={() => navigate(`/appointments/new?customerId=${customer._id}`)}
                      >
                        Schedule Appointment
                      </Button>
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Tabs section */}
        {!isNew && (
          <Grid item xs={12} md={8}>
            <Paper sx={{ height: '100%', boxShadow: 1 }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{ borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab label="Contact History" />
                <Tab label="Appointments" />
                <Tab label="Assessments" />
                <Tab label="Notes" />
              </Tabs>

              <Box p={3}>
                {/* Contact History Tab */}
                {tabValue === 0 && (
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Contact History
                    </Typography>
                    {customer?.contactHistory && customer.contactHistory.length > 0 ? (
                      <List>
                        {customer.contactHistory.map((contact, index) => (
                          <ListItem
                            key={index}
                            sx={{
                              border: '1px solid',
                              borderColor: contact.emergency ? 'error.main' : 'divider',
                              borderRadius: 1,
                              mb: 2,
                              bgcolor: contact.emergency ? 'error.light' : 'transparent',
                            }}
                          >
                            <ListItemText
                              primary={
                                <>
                                  <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <Typography variant="subtitle1" fontWeight="500" sx={{ color: contact.emergency ? 'error.dark' : 'inherit' }}>
                                      {contact.subject || 'Contact'}
                                      {contact.emergency && (
                                        <Chip 
                                          label="EMERGENCY" 
                                          color="error" 
                                          size="small" 
                                          sx={{ ml: 1, fontWeight: 'bold' }}
                                        />
                                      )}
                                    </Typography>
                                    <Typography variant="caption" color={contact.emergency ? 'error.dark' : 'text.secondary'}>
                                      {contact.date
                                        ? format(new Date(contact.date), 'MMMM dd, yyyy')
                                        : 'Unknown date'}
                                    </Typography>
                                  </Box>
                                </>
                              }
                              secondary={
                                <Typography
                                  variant="body2"
                                  sx={{ mt: 1, whiteSpace: 'pre-wrap', color: contact.emergency ? 'error.dark' : 'inherit' }}
                                >
                                  {contact.message || 'No message content'}
                                </Typography>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Typography color="text.secondary">No contact history found</Typography>
                    )}
                  </Box>
                )}

                {/* Appointments Tab */}
                {tabValue === 1 && (
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Appointments
                    </Typography>
                    {related.appointments.loading ? (
                      <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress size={26} />
                      </Box>
                    ) : related.appointments.error ? (
                      <Alert severity="error">
                        Couldn't load this customer's appointments. Please try again later.
                      </Alert>
                    ) : related.appointments.items.length > 0 ? (
                      <List disablePadding>
                        {related.appointments.items.map((appointment) => (
                          <ListItemButton
                            key={appointment._id}
                            onClick={() => navigate(`/appointments/${appointment._id}`)}
                            sx={{
                              borderRadius: '12px',
                              mb: 0.75,
                              border: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            <ListItemText
                              primary={
                                <Typography variant="subtitle2">
                                  {appointment.date
                                    ? format(new Date(appointment.date), 'EEEE, MMMM d, yyyy')
                                    : 'Date TBD'}
                                  {appointment.timeSlot ? ` · ${appointment.timeSlot}` : ''}
                                </Typography>
                              }
                              secondary={appointment.notes || null}
                              secondaryTypographyProps={{ noWrap: true }}
                            />
                            <Chip
                              size="small"
                              label={appointment.status || 'scheduled'}
                              color={
                                appointment.status === 'completed'
                                  ? 'success'
                                  : appointment.status === 'canceled'
                                  ? 'error'
                                  : appointment.status === 'rescheduled'
                                  ? 'warning'
                                  : 'primary'
                              }
                              sx={{ ml: 1, flexShrink: 0 }}
                            />
                          </ListItemButton>
                        ))}
                      </List>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No appointments found for this customer
                      </Typography>
                    )}
                    {customer && (
                      <Button
                        variant="contained"
                        color="primary"
                        sx={{ mt: 2 }}
                        onClick={() => navigate(`/appointments/new?customerId=${customer._id}`)}
                      >
                        Schedule Appointment
                      </Button>
                    )}
                  </Box>
                )}

                {/* Assessments Tab */}
                {tabValue === 2 && (
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Roof Assessments
                    </Typography>
                    {related.assessments.loading ? (
                      <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress size={26} />
                      </Box>
                    ) : related.assessments.error ? (
                      <Alert severity="error">
                        Couldn't load this customer's assessments. Please try again later.
                      </Alert>
                    ) : related.assessments.items.length > 0 ? (
                      <List disablePadding>
                        {related.assessments.items.map((assessment) => (
                          <ListItemButton
                            key={assessment._id}
                            onClick={() => navigate(`/assessments/${assessment._id}`)}
                            sx={{
                              borderRadius: '12px',
                              mb: 0.75,
                              border: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            <ListItemText
                              primary={
                                <Typography variant="subtitle2">
                                  {assessment.createdAt
                                    ? format(new Date(assessment.createdAt), 'MMMM d, yyyy')
                                    : 'Unknown date'}
                                </Typography>
                              }
                              secondary={assessment.result?.category || null}
                            />
                            <Chip
                              size="small"
                              label={`Score ${assessment.result?.score ?? '—'}`}
                              color={
                                (assessment.result?.score ?? 0) > 100
                                  ? 'error'
                                  : (assessment.result?.score ?? 0) > 50
                                  ? 'warning'
                                  : 'success'
                              }
                              sx={{ ml: 1, flexShrink: 0 }}
                            />
                          </ListItemButton>
                        ))}
                      </List>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No assessment records found for this customer
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Notes Tab */}
                {tabValue === 3 && (
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Notes
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {customer?.notes || 'No notes available for this customer'}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>
        )}
      </Grid>
      {/* Customer Assignment Dialog */}
      <AssignCustomerDialog 
        open={assignDialogOpen}
        onClose={handleCloseAssignDialog}
        customer={customer}
        onAssignSuccess={handleAssignSuccess}
      />
    </Box>
  );
};

export default CustomerDetail;